/**
 * Bulk "Change Color" modal.
 *
 * Shown only when every drawing of an order shares one color (the caller gates
 * on colorCount === 1). Lets the user pick a new Material then Color (color list
 * is filtered by the chosen material) and applies it to ALL drawings of the
 * order at once via POST /api/templates/unified/bulk-color-change, which
 * recomputes each drawing's price and propagates to Mongo + SWI.
 *
 * Material/color come from the same catalog endpoints the material-selection
 * screen uses (/fetch-core-product-data, /fetch-product-color-data/:id) so the
 * names line up with pricing and SWI.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
} from '@mui/material';
import axios from 'axios';
import swal from 'sweetalert2';
import { API_BASE_URL, tokenManager, userManager } from '../config/api.config';
import { logger } from '../utils/logger';

const authHeaders = () => ({
  'x-access-token': tokenManager.getToken(),
  Accept: 'application/json',
  'Content-Type': 'application/json',
});

const ChangeColorModal = ({ show, onClose, orderNumber, currentMaterial, currentColor, drawingCount = 0, onConfirmed }) => {
  const [materials, setMaterials] = useState([]); // [{ name, id, value }]
  const [colors, setColors] = useState([]);       // [{ name, value, code }]
  const [material, setMaterial] = useState('');
  const [color, setColor] = useState('');
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [loadingColors, setLoadingColors] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load the material catalog when the modal opens. Returns the list so the
  // open effect can immediately pre-select without waiting on a state update.
  const loadMaterials = useCallback(async () => {
    setLoadingMaterials(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/fetch-core-product-data`, { headers: authHeaders() });
      const items = res.data?.fetchedItems;
      if (Array.isArray(items)) {
        const list = items.map((item) => ({
          name: item.core_Product_Name,
          id: item._id,
          value: item.core_Product_Name || item._id,
        }));
        setMaterials(list);
        return list;
      }
    } catch (error) {
      logger.error('[ChangeColorModal] failed to load materials', error);
      swal.fire({ text: 'Failed to load materials', icon: 'error' });
    } finally {
      setLoadingMaterials(false);
    }
    return [];
  }, []);

  // Load the colours available for a chosen material. An optional
  // materialsList lets callers pass a freshly-fetched list (pre-select on open),
  // and preselectColor selects that colour when it exists in the list.
  const loadColors = useCallback(async (materialValue, materialsList, preselectColor) => {
    const list = materialsList || materials;
    const selected = list.find((m) => m.value === materialValue);
    if (!selected) return;
    setLoadingColors(true);
    setColors([]);
    try {
      const res = await axios.get(`${API_BASE_URL}/fetch-product-color-data/${selected.id}`, { headers: authHeaders() });
      if (Array.isArray(res.data)) {
        const colorsList = res.data.map((item) => ({
          name: item.product_Color || '',
          value: item.product_Color || '',
          code: item.product_Color_Code || '',
        }));
        setColors(colorsList);
        if (preselectColor && colorsList.some((c) => c.value === preselectColor)) {
          setColor(preselectColor);
        } else if (colorsList.length === 1) {
          // Auto-select when a material has a single colour.
          setColor(colorsList[0].value);
        }
      }
    } catch (error) {
      logger.error('[ChangeColorModal] failed to load colors', error);
      setColors([]);
    } finally {
      setLoadingColors(false);
    }
  }, [materials]);

  // Reset + load fresh on each open, pre-selecting the current material/colour.
  // Deps are the open flag + current values only — NOT loadColors, which is
  // recreated on every materials change and would re-fire this effect and flicker.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      setMaterial('');
      setColor('');
      setColors([]);
      const list = await loadMaterials();
      if (cancelled || !list || !list.length) return;
      if (currentMaterial && list.some((m) => m.value === currentMaterial)) {
        setMaterial(currentMaterial);
        await loadColors(currentMaterial, list, currentColor);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentMaterial, currentColor]);

  const handleMaterialChange = (value) => {
    setMaterial(value);
    setColor('');
    loadColors(value);
  };

  const handleApply = async () => {
    if (!material || !color) return;
    const confirm = await swal.fire({
      title: 'Change colour for all drawings?',
      html: `<div style="text-align:left">
        <b>${drawingCount}</b> drawing${drawingCount === 1 ? '' : 's'} will change`
        + (currentColor ? ` from <b>${currentColor}</b>` : '')
        + ` to <b>${color}</b> (material <b>${material}</b>).<br/><br/>`
        + `Prices will be recalculated. This cannot be undone.</div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, change colour',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d22530',
      cancelButtonColor: '#6B7280',
    });
    if (!confirm.isConfirmed) return;

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/templates/unified/bulk-color-change`,
        { orderNumber, material, color, enteredBy: userManager.getUserId() || null },
        { headers: authHeaders() }
      );
      setSubmitting(false);
      await swal.fire({
        title: 'Colour updated',
        text: `${res.data?.drawingsUpdated ?? drawingCount} drawing(s) updated to ${color}. Prices recalculated.`,
        icon: 'success',
      });
      if (onConfirmed) onConfirmed();
      onClose();
    } catch (error) {
      setSubmitting(false);
      const data = error.response?.data || {};
      if (data.code === 'PRICE_UNAVAILABLE' && Array.isArray(data.failed)) {
        const tags = data.failed.map((f) => f.tag || f.templateId).filter(Boolean).join(', ');
        swal.fire({
          title: 'Price not available',
          html: `These drawings cannot be repriced for <b>${material}</b>: <b>${tags || 'some drawings'}</b>.<br/><br/>`
            + `No customer price for that material/girth/fold. Please contact the accounts team. <b>Nothing was changed.</b>`,
          icon: 'error',
        });
      } else {
        swal.fire({
          text: data.error || data.message || 'Failed to change colour. Please try again.',
          icon: 'error',
        });
      }
    }
  };

  return (
    <Dialog open={!!show} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm" className="GeneralModal">
      <DialogTitle className="space-between">
        Change colour for all drawings
      </DialogTitle>
      <DialogContent>
        <p style={{ marginBottom: 16 }}>
          All <b>{drawingCount}</b> drawing{drawingCount === 1 ? '' : 's'} on this order
          {currentColor ? <> are currently <b>{currentColor}</b></> : null}. Pick a new material and colour to apply to every drawing.
        </p>

        <FormControl fullWidth size="small" style={{ marginBottom: 16 }}>
          <InputLabel id="ccm-material-label">Material</InputLabel>
          <Select
            labelId="ccm-material-label"
            label="Material"
            value={material}
            disabled={loadingMaterials || submitting}
            onChange={(e) => handleMaterialChange(e.target.value)}
          >
            {materials.map((m) => (
              <MenuItem key={m.id} value={m.value}>{m.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel id="ccm-color-label">Colour</InputLabel>
          <Select
            labelId="ccm-color-label"
            label="Colour"
            value={color}
            disabled={!material || loadingColors || submitting}
            onChange={(e) => setColor(e.target.value)}
          >
            {colors.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {(loadingMaterials || loadingColors) && (
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CircularProgress size={16} /> <span>Loading…</span>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} className="btn">Cancel</Button>
        <Button
          onClick={handleApply}
          disabled={!material || !color || submitting}
          className="btn primary-btn"
        >
          {submitting ? <CircularProgress size={18} /> : 'Apply to all drawings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeColorModal;
